// shared-lib/kitchen-assembly/styling/backsplashSpec.js
//
// A wall's own backsplash setting — `room.walls[i].backsplashSpec` — as the
// band builder reads it (contractor call item #9). The band is DERIVED from the
// cabinets and rebuilt by every writer, so a height drawn by hand never stuck;
// this is the one place a wall says how high its backsplash goes, or that it
// has none. It holds the RULE ("to the cabinets", "24 inches"), never a drawn
// size, so a band re-fits when a cabinet moves.
//
//   {
//     off: true,                               // no band on this wall
//     counter: { stop: 'cabinets' | 'full' | 'sill' | 'inches', inches },
//     range:   { stop: 'hood' | 'counter' | 'full' | 'inches', inches },
//   }
//
// - `inches` is measured up from the counter top. The card's 4" chip is
//   `{ stop: 'inches', inches: 4 }` — one rule, one name.
// - `full` is the same rule for both bands: the whole wall to its top but the
//   cabinets — up to the underside of whatever hangs over a stretch (an upper
//   over the counter; an over-range cabinet or microwave over the range) and
//   on ABOVE it to the top of the wall, where there is wall left
//   (`kitchenBacksplash`'s `bandStepsAboveUppers`). Never behind a cabinet.
//   The card says "Full height" for the counter and "To the ceiling" for the
//   range.
// - `sill` on a wall with no window over its counter is To the cabinets: the
//   rule has nothing to stop at.
//
// Absent — or any part of it — means today's automatic band. Pure.

const COUNTER_STOPS = Object.freeze(['cabinets', 'full', 'sill', 'inches']);
const RANGE_STOPS = Object.freeze(['hood', 'counter', 'full', 'inches']);
// The bathroom bands (step 3 of #9). `mirror`: to the underside of a mirror
// hung over the splash; `full`: to the top of the wall.
const VANITY_STOPS = Object.freeze(['inches', 'mirror']);
const TUB_STOPS = Object.freeze(['inches', 'full']);

/**
 * One band's setting, or null when it is missing or makes no sense. A
 * bathroom band may also be `{ off: true }` — a wall can carry a vanity and
 * a tub band, so each is removed on its own (a kitchen wall's `off` is the
 * wall's, both its bands together).
 */
function readBand(band, stops, { allowOff = false } = {}) {
  if (allowOff && band?.off === true) return { off: true };
  if (!band || !stops.includes(band.stop)) return null;
  if (band.stop !== 'inches') return { stop: band.stop, inches: null };
  const inches = Number(band.inches);
  return Number.isFinite(inches) && inches > 0
    ? { stop: 'inches', inches }
    : null;
}

/** The wall's setting, normalised: `{ off, counter, range }`. */
function readBacksplashSpec(wall) {
  const spec = wall?.backsplashSpec;
  return {
    off: spec?.off === true,
    counter: readBand(spec?.counter, COUNTER_STOPS),
    range: readBand(spec?.range, RANGE_STOPS),
    vanity: readBand(spec?.vanity, VANITY_STOPS, { allowOff: true }),
    tub: readBand(spec?.tub, TUB_STOPS, { allowOff: true }),
  };
}

/** Does this wall carry a setting for this band (`'counter'` | `'range'`)? */
function wallSetsBand(wall, band) {
  return readBacksplashSpec(wall)[band] !== null;
}

const { wallFaceSpanY } = require('../../geometry/wallFace');

const INCH_M = 0.0254;
// Two tops closer than this are one line (the builder's `SAME_UNDERSIDE_M`).
const SAME_TOP_M = 0.001;
// A sill must stand clear of the counter to be something to stop at.
const SILL_CLEAR_M = 0.01;

/**
 * The top of the wall's DRAWN face, in world Y (`wallFace`) — where a "Full
 * height" band stops. The stored box's top is not it on a face that follows
 * a raised or sloped ceiling.
 */
function wallTopY(wall) {
  return wallFaceSpanY(wall)?.top ?? wall?.center?.y ?? 0;
}

/**
 * The lowest sill of a window standing over this counter, or null.
 *
 * @param {Array<{min,max,bottomY,topY}>} openings the wall's opening extents
 *   (`openingExtentsByWall`), along-wall percent + world Y
 * @param {Array<{min,max}>} spans the counter's stretches, along-wall percent
 */
function sillOverCounter(openings, spans, counterTopY) {
  const sills = (openings || [])
    .filter(
      (o) =>
        o.bottomY > counterTopY + SILL_CLEAR_M &&
        (spans || []).some(
          (s) => Math.min(s.max, o.max) > Math.max(s.min, o.min)
        )
    )
    .map((o) => o.bottomY);
  return sills.length ? Math.min(...sills) : null;
}

/**
 * The counter band's LINE on a wall — where it stops wherever nothing hangs
 * over it — under the wall's setting, and whether that line also caps the band
 * under an upper (`capAtLine`: a 4" splash stays 4" under an upper hanging
 * higher; Full height and To the cabinets run up to the upper instead).
 *
 * @param {?{stop, inches}} counter the wall's counter setting, or null
 * @param {{cabinetsTopY, counterTopY, wallTopY, sillY}} at `cabinetsTopY` is
 *   today's line (To the cabinets); `sillY` from `sillOverCounter`
 */
function counterLineForSpec(counter, at) {
  switch (counter?.stop) {
    case 'full':
      return { lineY: at.wallTopY, capAtLine: false };
    case 'inches':
      return {
        lineY: at.counterTopY + counter.inches * INCH_M,
        capAtLine: true,
      };
    case 'sill':
      return at.sillY == null
        ? { lineY: at.cabinetsTopY, capAtLine: false }
        : { lineY: at.sillY, capAtLine: true };
    default:
      return { lineY: at.cabinetsTopY, capAtLine: false };
  }
}

/**
 * Steps (`bandStepsUnderUppers`) capped at the line where the setting says so,
 * neighbours left at one height merged back into one.
 */
function capStepsAtLine(steps, { lineY, capAtLine }) {
  if (!capAtLine) return steps;
  const out = [];
  for (const step of steps) {
    const topY = Math.min(step.topY, lineY);
    const last = out[out.length - 1];
    if (
      last &&
      last.max === step.min &&
      Math.abs(last.topY - topY) < SAME_TOP_M
    )
      last.max = step.max;
    else out.push({ ...step, topY });
  }
  return out;
}

/**
 * The range band's top under the wall's range setting.
 *
 * @param {{stop, inches}} range the setting
 * @param {object} at
 * @param {number} at.counterTopY
 * @param {number} at.counterLineY the counter band's line on this wall, under
 *   ITS setting — what Like the counter runs to
 * @param {number} at.wallTopY
 * @param {number[]} at.capBottoms undersides of what no tile goes behind over
 *   the range: an over-range cabinet or microwave
 * @param {number[]} at.hoodBottoms undersides of the hoods over the range
 */
function rangeTopForSpec(range, at) {
  const cap = at.capBottoms.length ? Math.min(...at.capBottoms) : Infinity;
  switch (range.stop) {
    case 'hood': {
      const under = [...at.capBottoms, ...at.hoodBottoms];
      return under.length ? Math.min(...under) : at.counterLineY;
    }
    case 'counter':
      return Math.min(cap, at.counterLineY);
    case 'full':
      return Math.min(cap, at.wallTopY);
    default:
      return Math.min(cap, at.counterTopY + range.inches * INCH_M);
  }
}

// Today's bathroom bands, and the default with no setting. In METRES as the
// browser has always written them, so a wall with no setting computes the
// same bits it did: 4" over a vanity, 12" over a tub.
const DEFAULT_VANITY_BACKSPLASH_M = 0.1016;
const DEFAULT_TUB_BACKSPLASH_M = 0.3048;

/** A custom height, stopped at the top of the wall when the wall is known. */
const customHeightM = (inches, baseY, wallTopY) =>
  wallTopY == null
    ? inches * INCH_M
    : Math.min(inches * INCH_M, wallTopY - baseY);

/**
 * How tall a vanity's backsplash runs above its counter, in metres.
 *
 * @param {?{stop, inches}} vanity the wall's vanity setting, or null
 * @param {{counterTopY: number, mirrorBottomY?: ?number,
 *   wallTopY?: ?number}} at the mirror's underside when one hangs over the
 *   splash; the wall's top, which a custom height stops at
 */
function vanityBandHeightM(
  vanity,
  { counterTopY, mirrorBottomY = null, wallTopY = null }
) {
  if (vanity?.stop === 'inches') {
    return customHeightM(vanity.inches, counterTopY, wallTopY);
  }
  if (vanity?.stop === 'mirror' && mirrorBottomY != null) {
    return mirrorBottomY - counterTopY;
  }
  return DEFAULT_VANITY_BACKSPLASH_M;
}

/**
 * How far a tub's backsplash runs above the tub's surface, in metres.
 *
 * @param {?{stop, inches}} tub the wall's tub setting, or null
 * @param {{tubTopY: number, wallTopY: number}} at
 */
function tubBandHeightM(tub, { tubTopY, wallTopY }) {
  if (tub?.stop === 'inches') {
    return customHeightM(tub.inches, tubTopY, wallTopY);
  }
  if (tub?.stop === 'full') return wallTopY - tubTopY;
  return DEFAULT_TUB_BACKSPLASH_M;
}

module.exports = {
  COUNTER_STOPS,
  RANGE_STOPS,
  VANITY_STOPS,
  TUB_STOPS,
  DEFAULT_VANITY_BACKSPLASH_M,
  DEFAULT_TUB_BACKSPLASH_M,
  vanityBandHeightM,
  tubBandHeightM,
  readBacksplashSpec,
  wallSetsBand,
  wallTopY,
  sillOverCounter,
  counterLineForSpec,
  capStepsAtLine,
  rangeTopForSpec,
};
